/**
 * Grouped lab panel table with reference ranges and flag highlighting.
 */
import { FC, useState } from 'react'
import type { LabGroup } from '../types'

export const LabGroupTable: FC<{ name: string; labs: LabGroup['labs'] }> = ({ name, labs }) => {
  const [open, setOpen] = useState(true)
  const abnormalCount = labs.filter(l => l.flag).length

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
      >
        <span>{name}</span>
        <div className="flex items-center gap-1.5">
          {abnormalCount > 0 && (
            <span className="text-[8px] font-bold text-clinical-coral bg-red-500/10 px-1.5 py-0.5 rounded-full">
              {abnormalCount} abnl
            </span>
          )}
          <span className="text-[9px]">{open ? '\u25BC' : '\u25B6'}</span>
        </div>
      </button>
      {open && (
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-card text-muted-foreground/60">
              <th className="text-left px-2.5 py-1 font-normal">Test</th>
              <th className="text-right px-2 py-1 font-normal">Value</th>
              <th className="text-right px-2 py-1 font-normal">Ref</th>
              <th className="text-center px-1.5 py-1 font-normal w-8"></th>
            </tr>
          </thead>
          <tbody>
            {labs.map((lab, i) => (
              <tr
                key={i}
                className={`border-t border-border ${
                  lab.flag === 'high' ? 'bg-red-500/5' : lab.flag === 'low' ? 'bg-blue-500/5' : ''
                }`}
              >
                <td className="px-2.5 py-1 text-muted-foreground">{lab.label}</td>
                <td className={`px-2 py-1 text-right font-mono font-medium ${
                  lab.flag === 'high' ? 'text-clinical-coral' : lab.flag === 'low' ? 'text-blue-400' : 'text-foreground'
                }`}>
                  {lab.value}
                  <span className="text-[9px] font-normal text-muted-foreground/60 ml-0.5">{lab.unit}</span>
                </td>
                <td className="px-2 py-1 text-right text-[9px] text-muted-foreground/60">{lab.refRange}</td>
                <td className="px-1.5 py-1 text-center">
                  {lab.flag ? (
                    <span className={`text-[8px] font-bold px-1 py-0.5 rounded ${
                      lab.flag === 'high' ? 'text-clinical-coral bg-red-500/10' : 'text-blue-400 bg-blue-500/10'
                    }`}>
                      {lab.flag === 'high' ? '\u2191' : '\u2193'}
                    </span>
                  ) : (
                    <span className="text-[9px] text-clinical-teal">{'\u2713'}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
